import { useTranslation } from 'react-i18next';
import React, { memo, useCallback, useMemo, useRef } from 'react';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';

import { EmptyRecordMobile, Loading } from '@/components';
import { useCurrentUserId } from '@/utils';

import { FeedListItem, LoadingSkeleton } from '../../components';
import { useFeedList, useNewStreamsCount } from '../../hooks';
import style from './index.module.scss';
import { IconShowMore } from '@/assets/stream';

export const ListContainer = memo(() => {
  const address = useCurrentUserId();
  const {
    result,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetchFirstPage,
    isFetching,
  } = useFeedList({ address });

  const listRef = useRef<VirtuosoHandle>();

  // resort stream with created at
  const lastStreamCreatedAt = useMemo(() => {
    return result?.[0]?.create_at;
  }, [result]);

  const isEmptyList = useMemo(() => !result?.length, [result]);

  const { data: newStreamsCount } = useNewStreamsCount(lastStreamCreatedAt);

  const fetchMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const fetchNewStreams = useCallback(() => {
    if (isFetching) {
      return;
    }

    refetchFirstPage();
    listRef.current?.scrollTo({ top: -68 });
  }, [isFetching, refetchFirstPage]);

  return (
    <div className={style.stream}>
      {address ? (
        <div className={style.streamListWrap}>
          {Number(newStreamsCount) > 0 ? (
            <div className={style.newStreamsCountWrap}>
              <div className={style.newStreamsCount} onClick={fetchNewStreams}>
                {isFetching ? (
                  <Loading />
                ) : (
                  <>
                    <IconShowMore />
                    <span>{`Show ${newStreamsCount} new`}</span>
                  </>
                )}
              </div>
            </div>
          ) : null}
          <div className={style.streamList}>
            {result?.length ? (
              <Virtuoso
                useWindowScroll
                data={result}
                overscan={10}
                endReached={fetchMore}
                itemContent={(index, item) => (
                  <FeedListItem
                    feed={item}
                    showMenu={false}
                    enableReadFeedback={false}
                    enableContributorId
                  />
                )}
                //@ts-ignore
                ref={listRef}
              />
            ) : null}

            {isLoading || isFetchingNextPage ? (
              <LoadingSkeleton />
            ) : isEmptyList ? (
              <EmptyStreamList />
            ) : null}
          </div>
        </div>
      ) : (
        <NotConnected />
      )}
    </div>
  );
});

const EmptyStreamList = () => {
  const { t } = useTranslation();

  return (
    <EmptyRecordMobile
      content={
        <>
          <p>{t('ListContainer.1r2cwyc')}</p>
          <p className={style.emptySubtitle}>{t('ListContainer.1bwsvc0')}</p>
        </>
      }
    />
  );
};
export const NotConnected = () => {
  const { t } = useTranslation();
  return <EmptyRecordMobile content={t('ListContainer.mobiledisable')} />;
};
